import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { login, verifyWebLoginOtp, sendWebLoginEmailOtp } from "../../services/authService";
import { useAuth } from "../../context/AuthContext";
import Input from "../../components/common/Input";
import Button from "../../components/common/Button";
import { APP_NAME } from "../../utils/constants";

const Login = () => {
  const navigate = useNavigate();
  const { login: setAuth } = useAuth();

  const [email, setEmail] = useState(() => localStorage.getItem("ps_remember_email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem("ps_remember_email"));
  const [loading, setLoading] = useState(false);

  // OTP flow state
  const [otpStep, setOtpStep] = useState(false);
  const [userId, setUserId] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [resending, setResending] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);
  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (rememberMe) {
      localStorage.setItem("ps_remember_email", email);
    } else {
      localStorage.removeItem("ps_remember_email");
    }

    try {
      const res = await login(email, password, true);
      const data = res.data;

      if (data?.Status === "OTP_REQUIRED") {
        setUserId(data.UserId);
        setOtpStep(true);
        toast.info(data.Message || "OTP sent to your mobile device");
        return;
      }

      const authData = data?.Result?.AuthData;
      const authToken = authData?.TokenInfo?.Token;

      if (!authToken) throw new Error("Login failed. Please try again.");

      setAuth(authData, authToken);
      toast.success("Login successful!");
      navigate("/dashboard");
    } catch (err) {
      const message =
        err.response?.data?.Message ||
        err.response?.data?.message ||
        err.message ||
        "Invalid credentials. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      await login(email, password, true);
      setOtp(["", "", "", ""]);
      otpRefs[0].current?.focus();
      toast.info("OTP resent to your mobile device");
    } catch (err) {
      const respData = err.response?.data;
      const message =
        (typeof respData === "string" ? respData : null) ||
        respData?.Message ||
        respData?.message ||
        "Failed to resend OTP. Please try again.";
      toast.error(message);
    } finally {
      setResending(false);
    }
  };

  const handleSendEmailOtp = async () => {
    setResending(true);
    try {
      await sendWebLoginEmailOtp(userId);
      setOtp(["", "", "", ""]);
      otpRefs[0].current?.focus();
      toast.info("OTP sent to your email");
    } catch (err) {
      const respData = err.response?.data;
      const message =
        (typeof respData === "string" ? respData : null) ||
        respData?.Message ||
        respData?.message ||
        "Failed to send email OTP. Please try again.";
      toast.error(message);
    } finally {
      setResending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (!pasted) return;
    const newOtp = ["", "", "", ""];
    for (let i = 0; i < pasted.length; i++) newOtp[i] = pasted[i];
    setOtp(newOtp);
    const focusIdx = Math.min(pasted.length, 3);
    otpRefs[focusIdx].current?.focus();
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== 4) {
      toast.error("Please enter the 4-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const res = await verifyWebLoginOtp(userId, otpValue, trustDevice);
      const authData = res.data?.Result?.AuthData;
      const authToken = authData?.TokenInfo?.Token;

      if (!authToken) throw new Error("Verification failed. Please try again.");

      // Mark device as trusted for this user
      if (trustDevice) {
        const trusted = JSON.parse(localStorage.getItem("ps_trusted_devices") || "[]");
        if (!trusted.includes(userId)) trusted.push(userId);
        localStorage.setItem("ps_trusted_devices", JSON.stringify(trusted));
      }

      setAuth(authData, authToken);
      toast.success("Login successful!");
      navigate("/dashboard");
    } catch (err) {
      const respData = err.response?.data;
      const message =
        (typeof respData === "string" ? respData : null) ||
        respData?.Message ||
        respData?.message ||
        "OTP verification failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* Blurred background image */}
      <div style={styles.bgImage} />
      <div style={styles.card}>
        <h2 style={styles.title}>{APP_NAME}</h2>
        <p style={styles.subtitle}>
          {otpStep ? "Enter the OTP sent to your mobile" : "Sign in to your account"}
        </p>

        {otpStep ? (
          <form onSubmit={handleVerifyOtp}>
            <div style={styles.otpContainer}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={otpRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  style={styles.otpInput}
                  autoFocus={i === 0}
                />
              ))}
            </div>
            <Button type="submit" disabled={loading} style={{ width: "100%", marginTop: 16 }}>
              {loading ? "Verifying..." : "Verify"}
            </Button>

            <label style={styles.trustRow}>
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.trustText}>Trust this device (skip OTP next time)</span>
            </label>

            <div style={styles.otpFooter}>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resending}
                style={styles.resendBtn}
              >
                {resending ? "Sending..." : "Resend OTP"}
              </button>
              <button
                type="button"
                onClick={handleSendEmailOtp}
                disabled={resending}
                style={styles.resendBtn}
              >
                {resending ? "Sending..." : "Get OTP on Email"}
              </button>
            </div>
            <button
              type="button"
              onClick={() => { setOtpStep(false); setOtp(["", "", "", ""]); setUserId(""); }}
              style={styles.backBtn}
            >
              &larr; Back to login
            </button>
          </form>
        ) : (
        <form onSubmit={handleSubmit}>
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
          />
          <div style={{ position: "relative" }}>
            <Input
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              style={styles.eyeBtn}
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>

          <label style={styles.rememberRow}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={styles.checkbox}
            />
            <span style={styles.rememberText}>Remember me</span>
          </label>

          <Button type="submit" disabled={loading} style={{ width: "100%", marginTop: 8 }}>
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },
  bgImage: {
    position: "absolute",
    inset: "-40px",
    backgroundImage: "url(/PS.png)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    filter: "blur(28px) brightness(0.35) saturate(1.3)",
    zIndex: 0,
  },
  card: {
    position: "relative",
    zIndex: 1,
    background: "rgba(255,255,255,0.92)",
    padding: 40,
    borderRadius: 16,
    boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
    width: "100%",
    maxWidth: 400,
    backdropFilter: "blur(8px)",
  },
  title: { margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#6c63ff", textAlign: "center" },
  subtitle: { margin: "0 0 28px", color: "#888", textAlign: "center", fontSize: 14 },
  eyeBtn: {
    position: "absolute", right: 10, top: 34,
    background: "none", border: "none", cursor: "pointer",
    fontSize: 16, padding: "4px 6px", lineHeight: 1,
    color: "#888",
  },
  rememberRow: {
    display: "flex", alignItems: "center", gap: 8,
    marginBottom: 8, cursor: "pointer", userSelect: "none",
  },
  checkbox: { accentColor: "#6c63ff", width: 16, height: 16, cursor: "pointer" },
  rememberText: { fontSize: 13, color: "#555" },
  trustRow: {
    display: "flex", alignItems: "center", gap: 8,
    marginTop: 12, cursor: "pointer", userSelect: "none",
    justifyContent: "center",
  },
  trustText: { fontSize: 12, color: "#666" },
  otpContainer: {
    display: "flex",
    justifyContent: "center",
    gap: 12,
    margin: "24px 0 8px",
  },
  otpInput: {
    width: 48,
    height: 54,
    textAlign: "center",
    fontSize: 22,
    fontWeight: 600,
    border: "2px solid #ddd",
    borderRadius: 10,
    outline: "none",
    transition: "border-color 0.2s",
    caretColor: "#6c63ff",
  },
  otpFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 16,
  },
  resendBtn: {
    background: "none",
    border: "none",
    color: "#888",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    textDecoration: "underline",
  },
  backBtn: {
    display: "block",
    margin: "12px auto 0",
    background: "none",
    border: "none",
    color: "#6c63ff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  },
};

export default Login;
